import { useState, useEffect, useCallback } from "react";

import Swal from "sweetalert2";

import { useNavigate, useParams } from "react-router-dom";

import { useAuthState } from "react-firebase-hooks/auth";
import { auth, db } from "../../../util/firebase";
import {
  query,
  collection,
  getDocs,
  where,
  runTransaction,
} from "firebase/firestore";

import BarLoader from "react-spinners/BarLoader";
import { FaChevronLeft } from "react-icons/fa";
import { Button, Modal, ModalBody } from "reactstrap";
import { GoDotFill } from "react-icons/go";
import { IoClose } from "react-icons/io5";
import memberships from "../../../config/memberships";
import { FREE_TO_PLAY } from "../../../config/appMode";

import { useTranslation } from "react-i18next";

function LeaguePage() {
  const { t } = useTranslation("common");

  const [user] = useAuthState(auth);
  const navigate = useNavigate();
  const params = useParams();
  const leagueUid = params.leagueUid;

  const [league, setLeague] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [userData, setUserData] = useState();
  const [publicRooms, setPublicRooms] = useState([]);
  const [typeModalIsOpen, setTypeModalIsOpen] = useState(false);
  const [authPromptOpen, setAuthPromptOpen] = useState(false);
  // modalRoom removed; availability and rooms per type are stored in modalRoomsByType
  const [modalLoadingAvailability, setModalLoadingAvailability] =
    useState(false);
  const [modalAvailability, setModalAvailability] = useState({});
  const [modalRoomsByType, setModalRoomsByType] = useState({});

  // Entry fee definitions (cash and tickets) per tournament type
  const ENTRY_FEES = {
    casual: {
      monthly: { cash: "$3", tickets: 300 },
      weekly: { cash: "$1", tickets: 100 },
    },
    standard: {
      monthly: { cash: "$5", tickets: 550 },
      weekly: { cash: "$1.75", tickets: 200 },
    },
    highStakes: {
      monthly: { cash: "$10", tickets: 1100 },
      weekly: { cash: "$2.50", tickets: 300 },
    },
    vipExclusive: {
      monthly: { cash: "$20", tickets: 2000 },
      weekly: { cash: "$20", tickets: 2000 },
    },
  };

  const getFeeText = (typeKey) => {
    const room = modalRoomsByType[typeKey];
    const fees = ENTRY_FEES[typeKey];
    if (!fees) return null;
    // use the room's configured durationType (weekly/monthly); fallback to monthly
    const duration = room?.durationType || "monthly";
    const userTier = userData?.membershipTier;
    const userExpiry = userData?.membershipExpiryDate;
    const hasActiveMembership =
      userTier &&
      userExpiry &&
      new Date(userExpiry.seconds * 1000) > new Date();
    // per SOW: discounts apply only to Monthly tournaments with active membership
    const discounted =
      duration === "monthly" &&
      hasActiveMembership &&
      memberships[userTier]?.discountedEntryFees?.[typeKey];
    const discountLabel = discounted ? " (discounted)" : "";
    if (duration === "weekly") {
      // weekly tournaments do not get membership discounts
      const tickets = fees.weekly.tickets;
      return `${fees.weekly.cash} / ${tickets} tickets`;
    }
    // monthly
    const tickets = discounted || fees.monthly.tickets;
    return `${fees.monthly.cash} / ${tickets} tickets${discountLabel}`;
  };

  const getTicketCost = (typeKey) => {
    const room = modalRoomsByType[typeKey];
    const fees = ENTRY_FEES[typeKey];
    if (!fees) return 0;
    const duration = room?.durationType || "monthly";
    const userTier = userData?.membershipTier;
    const userExpiry = userData?.membershipExpiryDate;
    const hasActiveMembership =
      userTier &&
      userExpiry &&
      new Date(userExpiry.seconds * 1000) > new Date();
    // per SOW: discounts apply only to Monthly tournaments with active membership
    const discounted =
      duration === "monthly" &&
      hasActiveMembership &&
      memberships[userTier]?.discountedEntryFees?.[typeKey];
    if (duration === "weekly") return fees.weekly.tickets;
    // monthly or fallback
    return discounted || fees.monthly.tickets;
  };

  // Free-to-play join: adds the user to the tournament with no entry fee
  // (no tickets / cash / VIP gate). Mirrors the member-add transaction used by
  // joinWithTickets, minus the ticket check and deduction.
  const joinFree = async (room) => {
    if (!userData) {
      Swal.fire({
        text: t("pleaseLoginToJoin") || "Please login to join.",
        icon: "info",
      });
      return;
    }
    if (!room) {
      Swal.fire({
        text: "That tournament is not available.",
        icon: "error",
      });
      return;
    }
    const confirm = await Swal.fire({
      text: t("joinThisTournament") || "Join this tournament?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "YES",
      cancelButtonText: "NO",
    });
    if (!confirm.isConfirmed) return;

    try {
      setIsLoading(true);
      const roomRefQ = query(
        collection(db, "rooms"),
        where("uid", "==", room.uid)
      );
      const roomDocs = await getDocs(roomRefQ);
      if (!roomDocs.docs.length) throw new Error("Room not found");
      const roomDocRef = roomDocs.docs[0].ref;

      const userRefQ = query(
        collection(db, "users"),
        where("uid", "==", user.uid)
      );
      const userDocs = await getDocs(userRefQ);
      if (!userDocs.docs.length) throw new Error("User doc not found");
      const userDocRef = userDocs.docs[0].ref;

      await runTransaction(db, async (tx) => {
        const rSnap = await tx.get(roomDocRef);
        const roomData = rSnap.data();
        if (roomData.memberUids?.includes(user.uid)) {
          // already a member
          return;
        }
        const uSnap = await tx.get(userDocRef);
        const uData = uSnap.data();

        const newMemberUids = Array.isArray(roomData.memberUids)
          ? [...roomData.memberUids]
          : [];
        if (!newMemberUids.includes(user.uid)) newMemberUids.push(user.uid);
        const newMembers = Array.isArray(roomData.members)
          ? [...roomData.members]
          : [];
        newMembers.push({
          name: uData.username,
          email: uData.email,
          points: 0,
          starPoints: 0,
          starCoins: 0,
          challengesWon: 0,
          total: 0,
          uid: uData.uid,
        });
        await tx.update(roomDocRef, {
          memberUids: newMemberUids,
          members: newMembers,
        });
      });

      await fetchUserData();
      await fetchPublicRooms();
      Swal.fire({
        text: t("joinedTournament") || "Joined tournament!",
        icon: "success",
      });
      navigate(`rooms/${room.uid}`);
    } catch (err) {
      console.error(err);
      Swal.fire({
        text: err.message || "Could not join tournament",
        icon: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const joinWithTickets = async (typeKey) => {
    if (!userData) {
      Swal.fire({
        text: t("pleaseLoginToJoin") || "Please login to join.",
        icon: "info",
      });
      return;
    }
    const room = modalRoomsByType[typeKey];
    if (!room) {
      Swal.fire({
        text: "That tournament type is not available.",
        icon: "error",
      });
      return;
    }
    const cost = getTicketCost(typeKey);
    // Block VIP Exclusive entries for non-Platinum members
    if (typeKey === "vipExclusive") {
      const userTier = userData?.membershipTier;
      const userExpiry = userData?.membershipExpiryDate;
      const hasActive =
        userExpiry && new Date(userExpiry.seconds * 1000) > new Date();
      if (!(hasActive && userTier === "platinum")) {
        Swal.fire({
          text: "VIP Exclusive tournaments are restricted to VIP Platinum members.",
          icon: "warning",
          confirmButtonColor: "#005EE1",
          confirmButtonText: "OKAY",
        });
        return;
      }
    }
    const confirm = await Swal.fire({
      text: `Spend ${cost} tickets to join this tournament?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "YES",
      cancelButtonText: "NO",
    });
    if (!confirm.isConfirmed) return;

    try {
      setIsLoading(true);
      // find room doc id
      const roomRefQ = query(
        collection(db, "rooms"),
        where("uid", "==", room.uid)
      );
      const roomDocs = await getDocs(roomRefQ);
      if (!roomDocs.docs.length) throw new Error("Room not found");
      const roomDocRef = roomDocs.docs[0].ref;

      // find user doc ref
      const userRefQ = query(
        collection(db, "users"),
        where("uid", "==", user.uid)
      );
      const userDocs = await getDocs(userRefQ);
      if (!userDocs.docs.length) throw new Error("User doc not found");
      const userDocRef = userDocs.docs[0].ref;

      await runTransaction(db, async (tx) => {
        const rSnap = await tx.get(roomDocRef);
        const roomData = rSnap.data();
        if (roomData.memberUids?.includes(user.uid)) {
          // already member
          return;
        }
        const uSnap = await tx.get(userDocRef);
        const uData = uSnap.data();
        const userTickets = uData.tickets || 0;
        if (userTickets < cost) {
          throw new Error("Not enough tickets");
        }
        // update user tickets
        await tx.update(userDocRef, { tickets: userTickets - cost });

        const newMemberUids = Array.isArray(roomData.memberUids)
          ? [...roomData.memberUids]
          : [];
        if (!newMemberUids.includes(user.uid)) newMemberUids.push(user.uid);
        const newMembers = Array.isArray(roomData.members)
          ? [...roomData.members]
          : [];
        newMembers.push({
          name: uData.username,
          email: uData.email,
          points: 0,
          starPoints: 0,
          starCoins: 0,
          challengesWon: 0,
          total: 0,
          uid: uData.uid,
        });
        await tx.update(roomDocRef, {
          memberUids: newMemberUids,
          members: newMembers,
        });
      });

      await fetchUserData();
      await fetchPublicRooms();
      Swal.fire({ text: "Joined tournament (tickets)!", icon: "success" });
      navigate(`rooms/${room.uid}`);
    } catch (err) {
      console.error(err);
      Swal.fire({
        text: err.message || "Could not join with tickets",
        icon: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const payWithCash = (typeKey) => {
    const room = modalRoomsByType[typeKey];
    if (!room) {
      Swal.fire({
        text: "That tournament type is not available.",
        icon: "error",
      });
      return;
    }
    const duration = room?.durationType || "monthly";
    // Block VIP Exclusive entries for non-Platinum members
    if (typeKey === "vipExclusive") {
      const userTier = userData?.membershipTier;
      const userExpiry = userData?.membershipExpiryDate;
      const hasActive =
        userExpiry && new Date(userExpiry.seconds * 1000) > new Date();
      if (!(hasActive && userTier === "platinum")) {
        Swal.fire({
          text: "VIP Exclusive tournaments are restricted to VIP Platinum members.",
          icon: "warning",
          confirmButtonColor: "#005EE1",
          confirmButtonText: "OKAY",
        });
        return;
      }
    }
    // get numeric price from ENTRY_FEES
    const cashStr =
      ENTRY_FEES[typeKey]?.[duration]?.cash ||
      ENTRY_FEES[typeKey]?.monthly?.cash;
    const priceNumeric =
      parseFloat((cashStr || "").replace(/[^0-9.]/g, "")) || 0;
    // navigate to checkout with entry params
    const params = new URLSearchParams({
      buy: "entry",
      roomUid: room.uid,
      type: typeKey,
      duration: duration,
      price: String(priceNumeric),
    });
    // App routing expects the checkout page under /shop/checkout
    navigate(`/shop/checkout?${params.toString()}`);
  };

  const fetchLeague = useCallback(async () => {
    try {
      setIsLoading(true);
      const q = query(collection(db, "leagues"), where("uid", "==", leagueUid));
      const docs = await getDocs(q);
      const data = docs.docs[0]?.data();
      setLeague(data);
      setIsLoading(false);
    } catch (err) {
      console.error(err);
      Swal.fire({
        text: "An error occured while fetching league",
        icon: "error",
      });
      setIsLoading(false);
    }
  }, [leagueUid]);

  const fetchUserData = useCallback(async () => {
    try {
      if (!user) return;
      const q = query(collection(db, "users"), where("uid", "==", user.uid));
      const docs = await getDocs(q);
      const data = docs.docs[0]?.data();
      setUserData(data);
    } catch (err) {
      console.error(err);
    }
  }, [user]);

  const fetchPublicRooms = useCallback(async () => {
    try {
      setIsLoading(true);
      // Query by league only then filter client-side so we support both
      // legacy rooms that have `type: 'public'` and new rooms that omit
      // `type` (we treat missing as public by convention).
      const q = query(
        collection(db, "rooms"),
        where("leagueUid", "==", leagueUid)
      );
      const docs = await getDocs(q);
      // Group rooms by season: admins may create one document per type
      // for the same season (casual, standard, etc.). We want a single
      // list entry per season with aggregated member counts.
      const seasonsMap = new Map();
      const now = new Date();
      docs.forEach((d) => {
        const r = d.data();
        const type = String(r.type || "public").toLowerCase();
        if (type !== "public") return; // skip non-public rooms

        // derive active state from explicit isActive OR opening/closing datetimes
        const parseTimestamp = (ts) => {
          if (!ts) return null;
          // Firestore Timestamp has seconds
          if (ts.seconds) return new Date(ts.seconds * 1000);
          // otherwise try Date constructor (string or number)
          const d = new Date(ts);
          return isNaN(d.getTime()) ? null : d;
        };

        const opening = parseTimestamp(
          r.openingDateTime || r.opening_date || r.openingDate
        );
        const closing = parseTimestamp(
          r.closingDateTime || r.closing_date || r.closingDate
        );
        const activeByDate = opening
          ? now >= opening && (!closing || now < closing)
          : null;

        // If an opening date is provided, honor it (don't allow join before opening)
        // otherwise fallback to the explicit isActive flag. An explicit
        // isActive === false acts as a hard off-switch even within the date window.
        const active = opening
          ? !!activeByDate && r.isActive !== false
          : !!r.isActive;

        // Determine membership and whether the tournament is past/upcoming.
        const isMember = user && r.memberUids?.includes(user.uid);
        const isPast = closing ? now >= closing : false;
        const isUpcoming = opening ? now < opening : false;

        // Hide past tournaments from non-members and hide tournaments that are
        // neither active nor upcoming for non-members. Members can still see their
        // tournaments (including past ones).
        if (isPast && !isMember) return;
        if (!active && !isUpcoming && !isMember) return;

        const seasonKey = r.season || "default";
        if (!seasonsMap.has(seasonKey)) {
          seasonsMap.set(seasonKey, {
            representative: r,
            memberUids: new Set(r.memberUids || []),
            isActive: active,
            createdAt: r.createdAt || 0,
            // keep all rooms for this season so we can resolve which exact
            // room a user belongs to when clicking the season entry
            rooms: [r],
          });
        } else {
          const entry = seasonsMap.get(seasonKey);
          (r.memberUids || []).forEach((uid) => entry.memberUids.add(uid));
          entry.isActive = entry.isActive || active;
          // add this room to the season's rooms list
          entry.rooms.push(r);
          if ((r.createdAt || 0) > (entry.createdAt || 0)) {
            entry.representative = r;
            entry.createdAt = r.createdAt || 0;
          }
        }
      });

      const fetched = Array.from(seasonsMap.entries()).map(([season, v]) => ({
        ...v.representative,
        season,
        // include aggregated memberUids/count and the rooms list for
        // deterministic navigation when user is a member
        memberUids: Array.from(v.memberUids),
        memberCount: v.memberUids.size,
        isActive: v.isActive,
        rooms: v.rooms || [],
      }));

      // sort newest first
      fetched.sort(
        (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
      );
      setPublicRooms(fetched);
      setIsLoading(false);
    } catch (err) {
      console.error(err);
      setIsLoading(false);
    }
  }, [leagueUid, user]);

  // Open the in-app auth prompt modal (matches the app's centeredModal idiom).
  const promptAuthToJoin = () => setAuthPromptOpen(true);
  const closeAuthPrompt = () => setAuthPromptOpen(false);

  // Stash the tournament URL so the user is returned here after authenticating,
  // then go to login or register.
  const goAuth = (path) => {
    try {
      localStorage.setItem("postAuthRedirect", `/play/leagues/${leagueUid}`);
    } catch (e) {
      // ignore
    }
    closeAuthPrompt();
    navigate(path);
  };

  const handleRoomClick = (room) => {
    // If user is already a member, go straight to the room
    if (room.memberUids?.includes(user?.uid)) {
      // We aggregate multiple room docs per season. The `room` here is the
      // representative, but the current user might belong to a different
      // room in the same season (e.g. highStakes vs vipExclusive). Prefer
      // navigating to the exact room where the user is a member, if found.
      const roomsList = room.rooms || [];
      const exact = roomsList.find((r) =>
        (r.memberUids || []).includes(user?.uid)
      );
      const targetUid = exact?.uid || room.uid;
      navigate(`rooms/${targetUid}`);
      return;
    }

    // Prevent opening the modal for "Coming soon" tournaments.
    // If the room is not active and its openingDateTime is in the future,
    // don't open the type picker for non-members.
    try {
      const parseOpening = (ts) => {
        if (!ts) return null;
        if (ts.seconds) return new Date(ts.seconds * 1000);
        const d = new Date(ts);
        return isNaN(d.getTime()) ? null : d;
      };
      const opening = parseOpening(
        room.openingDateTime || room.opening_date || room.openingDate
      );
      const now = new Date();
      const isUpcoming = opening ? now < opening : false;
      const isActive = !!room.isActive || false;
      if (!isActive && isUpcoming) {
        // Upcoming tournament: don't open/join, but tell the user when it opens
        // instead of silently doing nothing.
        Swal.fire({
          text: opening
            ? `${
                t("tournamentOpensOn") || "This tournament opens on"
              } ${opening.toLocaleString()}.`
            : t("tournamentNotOpenYet") ||
              "This tournament isn't open to join yet.",
          icon: "info",
        });
        return;
      }
    } catch (err) {
      // parsing failure — fall back to not blocking the modal
    }

    // Free-to-play: skip the tournament-type picker entirely and join the
    // single tournament directly (no tickets / cash / VIP gate).
    if (FREE_TO_PLAY) {
      // Logged-out visitors are prompted to log in or create an account first.
      if (!user) {
        promptAuthToJoin();
        return;
      }
      joinFree(room);
      return;
    }

    // open modal to pick a tournament type
    // setModalRoom intentionally not stored; modalRoomsByType will be fetched
    setTypeModalIsOpen(true);
    // fetch availability for the same league+season
    (async () => {
      try {
        setModalLoadingAvailability(true);
        // Try exact season match first (fast). If admins used a slightly
        // different season format, fall back to league-only and filter
        // by case-insensitive season comparison.
        let docsSnapshot;
        try {
          const q = query(
            collection(db, "rooms"),
            where("leagueUid", "==", room.leagueUid),
            where("season", "==", room.season)
          );
          docsSnapshot = await getDocs(q);
          // If no docs found, fall back to league-only query to try fuzzy match
          if (!docsSnapshot.docs.length) {
            const q2 = query(
              collection(db, "rooms"),
              where("leagueUid", "==", room.leagueUid)
            );
            docsSnapshot = await getDocs(q2);
          }
        } catch (err) {
          // In unlikely case of query failure, fall back to league-only query
          const q2 = query(
            collection(db, "rooms"),
            where("leagueUid", "==", room.leagueUid)
          );
          docsSnapshot = await getDocs(q2);
        }

        const avail = {};
        const roomsMap = {};
        docsSnapshot.forEach((d) => {
          const r = d.data();
          // Only consider rooms that match the season (if season field exists)
          if (r.season && room.season) {
            if (
              r.season.toString().toLowerCase() !==
              room.season.toString().toLowerCase()
            )
              return;
          }
          // Accept `tournamentType` with different casings or fallback to 'casual'
          const type = (
            r.tournamentType ||
            r.tournamenttype ||
            "casual"
          ).toString();
          avail[type] = (r.memberUids || []).length;
          roomsMap[type] = r;
        });
        setModalAvailability(avail);
        setModalRoomsByType(roomsMap);
      } catch (err) {
        console.error(err);
        setModalAvailability({});
        setModalRoomsByType({});
      } finally {
        setModalLoadingAvailability(false);
      }
    })();
  };

  const closeTypeModal = () => {
    setTypeModalIsOpen(false);
  };

  useEffect(() => {
    fetchLeague();
  }, [fetchLeague]);

  useEffect(() => {
    // Rooms are publicly readable, so load them even when logged out so a
    // shared tournament link is viewable. User-specific data only when logged in.
    fetchPublicRooms();
    if (user) {
      fetchUserData();
    }
  }, [user, fetchUserData, fetchPublicRooms]);

  if (isLoading)
    return (
      <div className="loadingPage">
        <img
          alt="logo"
          src="https://firebasestorage.googleapis.com/v0/b/sport-league-e35d7.appspot.com/o/assets%2Flogos%2F01%20-%20SL%20Logo%203.png?alt=media&token=2e5a6320-d2d7-45f8-9bc9-a8f9c7f8caa6"
          style={{ width: 140, marginBottom: "1rem" }}
        />
        <BarLoader color="#005EE1" />
      </div>
    );

  return (
    <div id="leaguePage" className="page">
      <div id="leaguePageHeader">
        <FaChevronLeft
          onClick={() => navigate(-1)}
          style={{
            color: "#005EE1",
            width: 35,
            height: 35,
            marginTop: -8,
            marginRight: "0.25rem",
            cursor: "pointer",
          }}
        />
        <img
          alt="leagueImage"
          src={
            league?.image
              ? league.image
              : "https://firebasestorage.googleapis.com/v0/b/sport-league-e35d7.appspot.com/o/assets%2Fleagues%2FleaguePlaceholder.png?alt=media&token=91a9596c-5be9-49ec-8917-d9da4f07b702"
          }
          style={{ maxHeight: 56 }}
        />
        <div>
          <p id="leaguePageHeaderName">{league?.name?.toUpperCase()}</p>
          <p id="leaguePageHeaderOrigin">{league?.origin?.toUpperCase()}</p>
        </div>
      </div>

      <p
        className="playPageHeader"
        style={{ marginBottom: "0.5rem", marginTop: "1rem" }}
      >
        {t("tournaments").toUpperCase()} ({publicRooms?.length || 0})
      </p>

      {publicRooms?.map((room) => (
        <div
          key={room.uid}
          onClick={() => handleRoomClick(room)}
          className="leagueRoomContainer"
        >
          <p>
            <GoDotFill
              style={{
                color: room.isActive ? "#25D366" : "red",
                marginRight: "0.5rem",
                width: 25,
                height: 25,
              }}
            />
            {room.season}
          </p>
          <p>
            {room.memberUids?.length || 0}{" "}
            {room.memberUids?.length > 1 || room.memberUids?.length === 0
              ? "members"
              : "member"}
          </p>
          {(() => {
            const isMember = room.memberUids?.includes(user?.uid);
            if (isMember) return null;
            if (room.isActive) {
              return (
                <Button
                  className="primaryBtn"
                  style={{ width: "fit-content", padding: "0.5rem 1rem" }}
                >
                  {t("join").toUpperCase()}
                </Button>
              );
            }
            // not active yet -> show Coming soon disabled button
            return (
              <Button
                className="secondaryBtn"
                disabled
                style={{ width: "fit-content", padding: "0.5rem 1rem" }}
              >
                {t("comingSoon").toUpperCase()}
              </Button>
            );
          })()}
        </div>
      ))}
      <Modal
        isOpen={typeModalIsOpen}
        toggle={closeTypeModal}
        className="centeredModal"
        size="lg"
      >
        <ModalBody>
          <div className="modalHeader">
            <div></div>
            <IoClose onClick={closeTypeModal} />
          </div>
          <div>
            <h1 className="modal-title-custom">
              {(
                t("chooseTournamentType") || "Choose tournament type"
              ).toUpperCase()}
            </h1>
            {/* duration is read from each room's durationType and shown per card */}
            <div>
              <div
                className="modalTypesContainer"
                style={{
                  display: "flex",
                  gap: "0.75rem",
                  justifyContent: "center",
                }}
              >
                {modalLoadingAvailability ? (
                  <BarLoader color="#005EE1" />
                ) : (
                  [
                    ["casual", (t("casual") || "Casual").toUpperCase()],
                    ["standard", (t("standard") || "Standard").toUpperCase()],
                    [
                      "highStakes",
                      (t("highStakes") || "High stakes").toUpperCase(),
                    ],
                    [
                      "vipExclusive",
                      (t("vipExclusive") || "VIP exclusive").toUpperCase(),
                    ],
                  ].map(([typeKey, label]) => {
                    const count = modalAvailability[typeKey] || 0;
                    const rooms = modalRoomsByType[typeKey];
                    const disabled = !rooms;
                    return (
                      <div
                        key={typeKey}
                        className="modalTypeCard"
                        style={{
                          minWidth: 140,
                          padding: 12,
                          borderRadius: 8,
                          background: "#0f1720",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                        }}
                      >
                        <div
                          className="modal-type-label"
                          style={{ fontSize: 14, marginBottom: 6 }}
                        >
                          {label}
                        </div>
                        <div style={{ fontSize: 12 }} className="count">
                          {count} {t("members") || "members"}
                        </div>
                        {/* show the duration type for this room (monthly/weekly) */}
                        <div
                          style={{
                            fontSize: 12,
                            color: "#d0d7df",
                            textAlign: "center",
                            marginTop: 6,
                          }}
                        >
                          {(
                            t(rooms?.durationType || "monthly") ||
                            rooms?.durationType ||
                            "monthly"
                          ).toUpperCase()}
                        </div>
                        <div
                          className="feeText"
                          style={{
                            fontSize: 12,
                            marginTop: 6,
                            marginBottom: 8,
                            textAlign: "center",
                          }}
                        >
                          {getFeeText(typeKey)}
                        </div>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 8,
                            width: "100%",
                          }}
                        >
                          <Button
                            className="primaryBtn"
                            disabled={disabled}
                            onClick={async () => {
                              await joinWithTickets(typeKey);
                              closeTypeModal();
                            }}
                            style={{ padding: "0.5rem 1rem", width: "100%" }}
                          >
                            {(t("payTickets") || "PAY TICKETS").toUpperCase()}
                          </Button>
                          <Button
                            className="secondaryBtn"
                            disabled={disabled}
                            onClick={() => {
                              payWithCash(typeKey);
                              closeTypeModal();
                            }}
                            style={{ padding: "0.4rem 0.8rem", width: "100%" }}
                          >
                            {(t("payCash") || "PAY CASH").toUpperCase()}
                          </Button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </ModalBody>
      </Modal>

      <Modal
        isOpen={authPromptOpen}
        toggle={closeAuthPrompt}
        className="centeredModal"
      >
        <ModalBody>
          <div className="modalHeader">
            <div></div>
            <IoClose onClick={closeAuthPrompt} />
          </div>
          <div style={{ textAlign: "center" }}>
            <h1 className="modal-title-custom">
              {(t("joinTournament") || "Join tournament").toUpperCase()}
            </h1>
            <p style={{ margin: "0.75rem 0 1.5rem" }}>
              {t("needAccountToJoin") ||
                "You need an account to join. Log in or create one to continue."}
            </p>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.75rem",
                maxWidth: 320,
                margin: "0 auto",
              }}
            >
              <Button className="primaryBtn" onClick={() => goAuth("/login")}>
                {(t("logIn") || "Log in").toUpperCase()}
              </Button>
              <Button
                className="secondaryBtn"
                onClick={() => goAuth("/register")}
              >
                {(t("createAccount") || "Create account").toUpperCase()}
              </Button>
            </div>
          </div>
        </ModalBody>
      </Modal>
    </div>
  );
}

export default LeaguePage;
